/* eslint-disable no-nested-ternary */
import React, {useMemo, useState} from 'react'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import {useTranslation} from 'react-i18next'
import {ILicenseItem, ListLicenses} from 'api/productApi'
import useProductManagement from 'features/productManagement/useProductManagement'
import useProductUsageHistory from 'features/productUsageHistory/useProductUsageHistory'
import useProductInstitutionOperationHistory from 'features/productInstitutionOperationHistory/useProductInstitutionOperationHistory'
import {MANUAL_PAYMENT_COLUMN} from 'pages/ProductUsageHistory/constant'
import {makeDateAndTimeFormat} from 'helpers/dateHelper'
import useProductUsageHistoryStyles from 'pages/ProductUsageHistory/style'
import DialogRegistrationManual from 'pages/ProductOperationOrganization/Component/DialogRegistrationManual'
import {PBM_INDICATOR_ID, TypeReasonCode} from 'pages/ProductsPages/constant'

const RechargeableBulkProduct = ({
  listItem,
  isAdmin = false,
  item,
  orgName = '',
  onUpdateLog,
}: {
  listItem: ListLicenses['items']
  isAdmin?: boolean
  item: ListLicenses | null
  orgName?: string
  onUpdateLog: () => void
}) => {
  const {listAllPackage, getQuantityLabel, getReasonCodeLabel} =
    useProductManagement()
  const {t, i18n} = useTranslation()
  const [showDetail, setShowDetail] = useState<boolean>(false)
  const [selectedPackage, setSelectedPackage] = useState<{
    packageItemId: string
    licenseId: string
    usageQuantity: number
    priority: number
    offerQuantity: number
    items: ILicenseItem[]
  }>()
  const classes = useProductUsageHistoryStyles()
  const {onSearchLicenseLogsWithId: onSearchUsage, listLicenseLogs: listUsage} =
    useProductUsageHistory()
  const {
    onSearchLicenseWithId: onSearchInstitution,
    listLicenseLogs: listInstitution,
  } = useProductInstitutionOperationHistory()
  const [isOpenManualRegistration, setIsOpenManualRegistration] =
    useState<boolean>(false)

  const listLicenseLogs = useMemo(() => {
    return isAdmin ? listInstitution : listUsage
  }, [isAdmin, listInstitution, listUsage])

  const getInfoPackage = (packageId: string) => {
    let name = ''
    const item = listAllPackage?.find((pkg) => pkg.itemId === packageId)
    if (item) {
      name =
        item.displayInfos.find(
          (it) => it.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
        )?.name || ''
    }
    return name
  }

  const mergeListItem = useMemo(() => {
    // Group items by packageItemId
    const grouped = listItem.reduce<
      Record<
        string,
        {
          packageItemId: string
          licenseId: string
          usageQuantity: number
          priority: number
          offerQuantity: number
          items: ILicenseItem[]
        }
      >
    >((acc, curr) => {
      const key = curr.packageItemId || ''
      if (!acc[key]) {
        acc[key] = {
          packageItemId: key,
          licenseId: curr.licenseId || '',
          usageQuantity: 0,
          offerQuantity: 0,
          priority: 0,
          items: [],
        }
      }
      acc[key].usageQuantity += curr.usageQuantity
      acc[key].priority += curr.priority > 0 ? curr.offerQuantity : 0
      acc[key].offerQuantity += curr.priority === 0 ? curr.offerQuantity : 0
      acc[key].items.push(curr)
      return acc
    }, {})

    // Convert grouped object to array
    return Object.values(grouped)
  }, [listItem, listLicenseLogs])

  const detailTableData = useMemo(() => {
    if (!selectedPackage) return []
    const packageItems = selectedPackage?.items || []
    const offerLogs = listLicenseLogs?.offerLogs || []

    const filteredByPackageItemId = offerLogs
      .filter((offer) =>
        packageItems.some(
          (it) =>
            it.id === offer.licenseItemId &&
            it.packageItemId === selectedPackage.packageItemId &&
            (offer.method === 'PAYMENT_GOODS' || offer.method === 'MANUAL'),
        ),
      )
      .map((item) => ({
        ...item,
        type: 'OFFER',
      }))

    const autoMethodOffer = offerLogs
      .filter((offer) =>
        packageItems.some(
          (it) =>
            it.id === offer.licenseItemId &&
            it.packageItemId === selectedPackage.packageItemId &&
            offer.method === 'AUTO',
        ),
      )
      .map((item) => ({
        ...item,
        type: 'AUTO',
      }))

    const getMethodLabel = (method: string) => {
      switch (method) {
        case 'PAYMENT_GOODS':
          return t('IPurchasePackage')
        case 'AUTO':
          return t('IAutomaticPayment')
        case 'PAYMENT_CHARGE':
          return t('IChargeV1')
        case 'MANUAL':
          return t('IManualPayment')
        default:
          return method
      }
    }

    const listUsage = listLicenseLogs?.usageLogs
      ?.filter((item) =>
        filteredByPackageItemId?.some(
          (offer) => offer.licenseItemId === item.licenseItemId,
        ),
      )
      .map((item) => ({
        ...item,
        method:
          filteredByPackageItemId.find(
            (offer) => offer.licenseItemId === item.licenseItemId,
          )?.method || '',
        type: 'USAGE',
      }))

    return [
      ...filteredByPackageItemId,
      ...autoMethodOffer,
      ...(listUsage || []),
    ]
      .map((item) => ({
        usageDateTime: makeDateAndTimeFormat(
          new Date(
            item.type === 'OFFER' || item.type === 'AUTO'
              ? (item as {offerDateTime: number}).offerDateTime
              : (item as {usageDateTime: number}).usageDateTime,
          ),
        ),
        detail: getInfoPackage(selectedPackage.packageItemId),
        method:
          item.type === 'USAGE' &&
          (item as {usageQuantity: number}).usageQuantity > 0
            ? t('IUse')
            : getMethodLabel(item.method),
        manualPayment:
          item.method === 'MANUAL' ||
          (item.method === 'PAYMENT_GOODS' && item.type === 'OFFER') ||
          item.type === 'AUTO'
            ? getQuantityLabel((item as {offerQuantity: number}).offerQuantity)
            : 0,
        rechargeCurrentMonth:
          item.method === 'PAYMENT_CHARGE' && item.type === 'OFFER'
            ? getQuantityLabel((item as {offerQuantity: number}).offerQuantity)
            : 0,
        usageId:
          item.type === 'USAGE' ? (item as {usageId: string}).usageId : '-',
        use:
          item.type === 'USAGE'
            ? (item as {usageQuantity: number}).usageQuantity
            : 0,
        reason:
          item.type === 'OFFER'
            ? isAdmin
              ? (item as {reason: string}).reason
              : getReasonCodeLabel(
                  (item as {reasonCode: TypeReasonCode}).reasonCode,
                )
            : item.type === 'AUTO'
            ? isAdmin
              ? t('IAnalysisFailed')
              : getReasonCodeLabel(
                  (item as {reasonCode: TypeReasonCode}).reasonCode,
                )
            : '-',
      }))
      .reverse()
  }, [selectedPackage, listLicenseLogs, item, i18n.language])

  const handleGetLicenseLog = (item: string) => {
    if (isAdmin) onSearchInstitution(item)
    else onSearchUsage(item)
    setShowDetail(true)
  }
  return (
    <div style={{marginTop: '15px'}}>
      <span>* {t('IAnalysesNumber')}</span>
      <div className={classes.productFlex}>
        {mergeListItem.map((item) => (
          /* eslint-disable jsx-a11y/click-events-have-key-events */
          /* eslint-disable jsx-a11y/no-static-element-interactions */
          <div
            key={item.packageItemId}
            className={classes.cardAnalysisNumber}
            style={{
              backgroundColor:
                item.packageItemId === selectedPackage?.packageItemId
                  ? '#e0f7f7'
                  : '',
            }}
            onClick={() => {
              setSelectedPackage(item)
              handleGetLicenseLog(item.licenseId)
            }}
          >
            <div className={classes.headerAnalysisNumber}>
              <div className={classes.titleAnalysisNumber}>
                {`${getInfoPackage(item.packageItemId)} - ${
                  item.offerQuantity > 10000000
                    ? t('IUnlimited')
                    : `${item.offerQuantity + item.priority} ${
                        item.packageItemId === PBM_INDICATOR_ID
                          ? t('IMinute')
                          : t('ICase')
                      }`
                }`}
              </div>
              <div className={classes.mainValueAnalysisNumber}>
                {item.offerQuantity > 10000000
                  ? t('IUnlimited')
                  : item.offerQuantity + item.priority - item.usageQuantity}
              </div>
            </div>
            <div className={classes.metricsValueAnalysisNumber}>
              <div className={`${classes.metric}`}>
                <div className={classes.metricLabel}>{t('IPaymentV1')}</div>
                <div className={classes.metricValue}>
                  {item.offerQuantity > 10000000
                    ? t('IUnlimited')
                    : item.offerQuantity}
                </div>
              </div>
              <div className={`${classes.metric}`}>
                <div className={classes.metricLabel}>{t('IChargeV1')}</div>
                <div className={classes.metricValue}>{item.priority}</div>
              </div>
              <div className={`${classes.metric}`}>
                <div className={classes.metricLabel}>{t('IUse')}</div>
                <div className={classes.metricValue}>{item.usageQuantity}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showDetail && (
        <>
          <div className={classes.btnSpec}>
            {isAdmin && (
              <Button
                color='primary'
                variant='contained'
                onClick={() => setIsOpenManualRegistration(true)}
              >
                {t('IManualRegistration')}
              </Button>
            )}
          </div>
          <Table size='small' className={classes.table}>
            <TableHead className={classes.tableHeader}>
              {MANUAL_PAYMENT_COLUMN.map((item) => (
                <TableCell
                  align='center'
                  padding='checkbox'
                  key={item.id}
                  style={{
                    whiteSpace: 'pre',
                    width: item.key === 'period' ? 320 : 'auto',
                  }}
                >
                  {t(item.label)}
                </TableCell>
              ))}
            </TableHead>
            <TableBody>
              {detailTableData?.map((row, index) => (
                <TableRow key={`${row.usageId}-${index}`}>
                  {MANUAL_PAYMENT_COLUMN.map((col) => {
                    return col.key === 'index' ? (
                      <TableCell align='center'>
                        <span>{detailTableData.length - index}</span>
                      </TableCell>
                    ) : (
                      <TableCell key={col.id} align='center'>
                        {row[col.key]}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
      {isAdmin && (
        <DialogRegistrationManual
          open={isOpenManualRegistration}
          onClose={() => setIsOpenManualRegistration(false)}
          onUpdateLog={() => {
            onUpdateLog()
            onSearchInstitution(item?.id || '')
          }}
          item={item as ListLicenses}
          orgName={orgName}
        />
      )}
    </div>
  )
}

export default RechargeableBulkProduct
